import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';

import { ParamsUsedInRequest } from './schemas';
import { OptionalStartupable } from '../base/Startupable';
import {
  isStartupableStarted,
  registerStartupable,
} from '../base/StartupHelper';
import PaymentStore from '../payments/PaymentStore';
import TelemetryService from '../telemetry/TelemetryService';
import { getKlarnaUrl } from './helpers/WorldpayApi';

class WorldpayKlarnaService implements OptionalStartupable {
  public readonly name = 'WorldpayKlarnaService';
  public readonly moduleId = PaymentModuleId.WorldpayKlarna;

  private _requestData: ParamsUsedInRequest | undefined;
  private _redirectUrl: string | undefined;

  constructor() {
    registerStartupable(this, [PaymentStore]);
  }

  public shouldStart(): boolean {
    return PaymentStore.allowedPaymentModules.includes(this.moduleId);
  }

  public startup(): Promise<void> {
    this._requestData = undefined;
    this._redirectUrl = undefined;
    return Promise.resolve();
  }

  public async getRedirectUrl(
    locale: SupportedLanguage,
    accessToken: string,
  ): Promise<string> {
    const redirectUrl = this._redirectUrl;
    const telemetryConsent = isStartupableStarted(TelemetryService);
    const paymentIntentId = PaymentStore.paymentIntentId;

    if (
      this.shouldReturnCachedUrl(paymentIntentId, locale, telemetryConsent) &&
      redirectUrl
    ) {
      return Promise.resolve(redirectUrl);
    }

    const url = await getKlarnaUrl(
      paymentIntentId,
      accessToken,
      locale,
      telemetryConsent,
    );
    this._redirectUrl = url;
    this._requestData = {
      paymentIntentId,
      locale,
      telemetryConsent,
    };
    return url;
  }

  private shouldReturnCachedUrl(
    paymentIntentId: string,
    locale: SupportedLanguage,
    telemetryConsent: boolean,
  ) {
    return (
      this._requestData &&
      this._requestData.paymentIntentId === paymentIntentId &&
      this._requestData.locale === locale &&
      this._requestData.telemetryConsent === telemetryConsent
    );
  }
}

export default new WorldpayKlarnaService();
